import { ReactNode, useState, useEffect, useMemo, useRef, useCallback, Suspense } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { WorkspaceSearch } from "@/components/admin/WorkspaceSearch";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { isSubAdminAllowedPath } from "@/lib/subAdminAccess";
import {
  LayoutDashboard,
  Building2,
  CalendarDays,
  Users,
  Database,
  Target,
  Settings,
  Linkedin,
  Menu,
  RefreshCw,
  ListChecks,
  Gauge,
  Shield,
  LogOut,
  Mail,
  ChevronDown,
  Search,
  Bell,
  IndianRupee,
  UserCog,
  CreditCard,
  Megaphone,
  Activity,
  MessageSquare,
  TrendingUp,
  Archive,
  AlertCircle,
  Sparkles,
  BarChart3,
  Cog,
  Send,
  MessageCircle,
  Handshake,
  Clock,
  ChevronRight,
  UserMinus,
  UserSearch,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { fetchLiveness } from "@/lib/accountLiveness";
import { fetchIdleAccounts } from "@/lib/idleAccounts";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

interface AdminLayoutProps {
  children: ReactNode;
}

interface NavItem {
  name: string;
  href?: string;
  icon: any;
  badge?: number;
  onHover?: () => void;
  children?: NavItem[];
}

interface NavigationSection {
  title: string;
  items: NavItem[];
}

// Hoisted SidebarContent — no re-creation per render of AdminLayout
interface SidebarContentProps {
  navigationSections: NavigationSection[];
  pathname: string;
  onItemClick: () => void;
  isClearing: boolean;
  onClearCache: () => void;
  onLogout: () => void;
}

const isItemActive = (href: string | undefined, pathname: string) => {
  if (!href) return false;
  return pathname === href || (href !== "/admin" && pathname.startsWith(href));
};

const NavLeaf = ({
  item,
  pathname,
  onItemClick,
  nested = false,
}: {
  item: NavItem;
  pathname: string;
  onItemClick: () => void;
  nested?: boolean;
}) => {
  const isActive = isItemActive(item.href, pathname);
  const Icon = item.icon;
  return (
    <Link
      to={item.href!}
      onClick={onItemClick}
      onMouseEnter={item.onHover}
      onFocus={item.onHover}
      className={cn(
        "group flex items-center justify-between gap-3 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200",
        nested && "pl-9 py-1.5 text-[13px]",
        isActive
          ? "bg-primary/10 text-primary"
          : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
      )}
    >
      <div className="flex items-center gap-3">
        {!nested && (
          <Icon
            className={cn(
              "w-4 h-4 transition-colors",
              isActive ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
            )}
          />
        )}
        {nested && <Icon className={cn("w-3.5 h-3.5", isActive ? "text-primary" : "text-muted-foreground")} />}
        {item.name}
      </div>
      {item.badge && item.badge > 0 && (
        <Badge variant="destructive" className="h-5 min-w-5 px-1.5 text-xs">
          {item.badge}
        </Badge>
      )}
    </Link>
  );
};

const NavGroup = ({
  item,
  pathname,
  onItemClick,
}: {
  item: NavItem;
  pathname: string;
  onItemClick: () => void;
}) => {
  const storageKey = `adminSidebar:group:${item.name}`;
  const hasActiveChild = (item.children || []).some((c) => isItemActive(c.href, pathname));
  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return hasActiveChild;
    const saved = window.localStorage.getItem(storageKey);
    if (saved === "1") return true;
    if (saved === "0") return hasActiveChild ? true : false;
    return hasActiveChild;
  });
  useEffect(() => {
    if (hasActiveChild) setOpen(true);
  }, [hasActiveChild]);
  const toggle = () => {
    setOpen((prev) => {
      const next = !prev;
      try { window.localStorage.setItem(storageKey, next ? "1" : "0"); } catch {}
      return next;
    });
  };
  const Icon = item.icon;
  const aggregateBadge =
    !open
      ? (item.children || []).reduce((sum, c) => sum + (c.badge || 0), 0)
      : 0;
  return (
    <div>
      <button
        type="button"
        onClick={toggle}
        className={cn(
          "group w-full flex items-center justify-between gap-3 px-3 py-2 text-sm font-medium rounded-md transition-all duration-200",
          hasActiveChild
            ? "text-primary"
            : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        )}
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <Icon
            className={cn(
              "w-4 h-4 transition-colors",
              hasActiveChild ? "text-primary" : "text-muted-foreground group-hover:text-foreground"
            )}
          />
          {item.name}
        </div>
        <div className="flex items-center gap-2">
          {aggregateBadge > 0 && (
            <Badge variant="destructive" className="h-5 min-w-5 px-1.5 text-xs">
              {aggregateBadge}
            </Badge>
          )}
          <ChevronRight
            className={cn(
              "w-3.5 h-3.5 text-muted-foreground transition-transform",
              open && "rotate-90"
            )}
          />
        </div>
      </button>
      {open && (
        <div className="mt-1 space-y-0.5">
          {item.children!.map((child) => (
            <NavLeaf
              key={child.name}
              item={child}
              pathname={pathname}
              onItemClick={onItemClick}
              nested
            />
          ))}
        </div>
      )}
    </div>
  );
};



const SidebarContent = ({
  navigationSections,
  pathname,
  onItemClick,
  isClearing,
  onClearCache,
  onLogout,
}: SidebarContentProps) => {
  const { isActuallySubAdmin, isActuallySuperAdmin } = useWorkspace();
  const isSubAdminView = isActuallySubAdmin && !isActuallySuperAdmin;
  return (
  <div className="flex flex-col h-full bg-sidebar border-r border-sidebar-border">
    <div className="p-4 h-16 flex items-center border-b border-sidebar-border">
      <div className="flex items-center gap-3 w-full">
        <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center shrink-0 shadow-sm">
          <Shield className="w-4 h-4 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold text-sidebar-foreground truncate leading-none">
            Admin Console
          </h2>
          <p className="text-xs text-muted-foreground mt-1 truncate">
            Platform Administration
          </p>
        </div>
      </div>
    </div>

    <div className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
      {navigationSections.map((section) => (
        <div key={section.title}>
          <h3 className="px-3 mb-2 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {section.title}
          </h3>
          <div className="space-y-1">
            {section.items.map((item) =>
              item.children && item.children.length > 0 ? (
                <NavGroup
                  key={item.name}
                  item={item}
                  pathname={pathname}
                  onItemClick={onItemClick}
                />
              ) : (
                <NavLeaf
                  key={item.name}
                  item={item}
                  pathname={pathname}
                  onItemClick={onItemClick}
                />
              )
            )}
          </div>
        </div>
      ))}
    </div>


    <div className="p-4 border-t border-sidebar-border">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="w-full justify-start px-2 hover:bg-sidebar-accent h-auto py-2">
            <div className="flex items-center gap-3 w-full">
              <Avatar className="h-8 w-8 border border-border">
                <AvatarFallback className="bg-destructive/10 text-destructive text-xs">
                  {isSubAdminView ? "SA" : "SU"}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 text-left min-w-0">
                <p className="text-sm font-medium text-sidebar-foreground truncate">
                  {isSubAdminView ? "Sub Admin" : "Super Admin"}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {isSubAdminView ? "Assigned workspaces" : "System Access"}
                </p>
              </div>
              <ChevronDown className="w-4 h-4 text-muted-foreground" />
            </div>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" sideOffset={8}>
          <DropdownMenuLabel>System Controls</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onClearCache} disabled={isClearing}>
            <RefreshCw className={cn("mr-2 h-4 w-4", isClearing && "animate-spin")} />
            <span>Clear Cache</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onLogout} className="text-destructive focus:text-destructive">
            <LogOut className="mr-2 h-4 w-4" />
            <span>Log out</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  </div>
);
};

export const AdminLayout = ({ children }: AdminLayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { isActuallySubAdmin, isActuallySuperAdmin } = useWorkspace();
  const isSubAdminView = isActuallySubAdmin && !isActuallySuperAdmin;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const queryClient = useQueryClient();

  // Warm both the JS chunk and the tokens query for the Self-Serve Links page
  // when the sidebar link is hovered/focused. Idempotent — react-query dedupes.
  const prefetchSelfServeLinks = useCallback(() => {
    import("@/pages/admin/SelfServeLinks").catch(() => {});
    queryClient.prefetchQuery({
      queryKey: ["self-serve-tokens"],
      queryFn: async () => {
        const { data, error } = await supabase
          .from("self_serve_onboarding_tokens" as any)
          .select("id, token, billing_cycle, currency, seat_count, notes, expires_at, is_used, claimed_at, consumed_user_email, revoked, created_at")
          .order("created_at", { ascending: false });
        if (error) throw error;
        return data as any[];
      },
      staleTime: 60_000,
    }).catch(() => {});
  }, [queryClient]);


  // Defer ALL sidebar badge queries until after first paint of the page so
  // route navigation feels instant. Badges (Badge only renders when count>0)
  // appear a beat later with no layout shift.
  const [enableBadges, setEnableBadges] = useState(false);
  useEffect(() => {
    const schedule =
      (window as any).requestIdleCallback?.bind(window) ||
      ((cb: () => void) => setTimeout(cb, 1500));
    const handle = schedule(() => setEnableBadges(!isSubAdminView), { timeout: 2500 });
    return () => {
      if ((window as any).cancelIdleCallback && typeof handle === "number") {
        (window as any).cancelIdleCallback(handle);
      }
    };
  }, []);

  // Warm up the most-used admin page chunks during idle time so subsequent
  // sidebar clicks feel instant. We import the modules without rendering them;
  // Vite/webpack cache the chunks. Errors are swallowed (network/auth issues
  // shouldn't surface here).
  useEffect(() => {
    const schedule =
      (window as any).requestIdleCallback?.bind(window) ||
      ((cb: () => void) => setTimeout(cb, 2000));
    const handle = schedule(
      () => {
        const prefetchers = [
          () => import("@/pages/admin/Organizations"),
          () => import("@/pages/admin/Users"),
          () => import("@/pages/admin/TargetDatabase"),
          () => import("@/pages/admin/IcpAnalyzer"),
          () => import("@/pages/admin/CampaignMonitor"),
          () => import("@/pages/admin/ManagedCampaigns"),
          () => import("@/pages/admin/OrphanCampaigns"),
          () => import("@/pages/admin/MasterAccounts"),
          () => import("@/pages/admin/NotificationSettings"),
          () => import("@/pages/admin/SelfServeLinks"),
        ];
        // Stagger so we don't blast the network all at once.
        prefetchers.forEach((p, i) => {
          setTimeout(() => {
            p().catch(() => { /* ignore prefetch errors */ });
          }, i * 120);
        });
      },
      { timeout: 4000 }
    );
    return () => {
      if ((window as any).cancelIdleCallback && typeof handle === "number") {
        (window as any).cancelIdleCallback(handle);
      }
    };
  }, []);

  // Pending managed campaign requests count — cached + realtime (filtered)
  const { data: pendingManagedCampaigns = 0, refetch: refetchPendingManaged } = useQuery({
    queryKey: ["admin-pending-managed-count"],
    queryFn: async () => {
      const { count, error } = await supabase
        .from("managed_campaign_requests")
        .select("*", { count: "exact", head: true })
        .in("status", ["pending_setup", "in_progress"]);
      if (error) return 0;
      return count || 0;
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: enableBadges,
  });

  // Campaigns clamped to 0/0 daily limits at creation — need ops to assign limits



  // Disconnected LinkedIn accounts count — platform-wide
  const { data: disconnectedAccountsCount = 0 } = useQuery({
    queryKey: ["admin-disconnected-accounts-count"],
    queryFn: async () => {
      const { count } = await supabase
        .from("clients")
        .select("*", { count: "exact", head: true })
        .eq("status", "disconnected")
        .not("unipile_account_id", "is", null)
        .eq("is_demo", false);
      return count || 0;
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: enableBadges,
  });

  // CRM follow-ups due today and not done (RLS scopes sub admins to their workspaces)
  const [crmBadgeReady, setCrmBadgeReady] = useState(false);
  useEffect(() => { const t = setTimeout(() => setCrmBadgeReady(true), 1500); return () => clearTimeout(t); }, []);
  const { data: crmTodayCount = 0 } = useQuery({
    queryKey: ["admin-crm-today-count"],
    queryFn: async () => {
      const from = new Date(); from.setHours(0, 0, 0, 0);
      const to = new Date(from); to.setDate(to.getDate() + 1);
      const { count } = await (supabase as any)
        .from("workspace_crm_interactions")
        .select("id", { count: "exact", head: true })
        .eq("follow_up_done", false)
        .gte("follow_up_at", from.toISOString())
        .lt("follow_up_at", to.toISOString());
      return count || 0;
    },
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    enabled: crmBadgeReady,
  });

  // Accounts in a LinkedIn connection cooldown — platform-wide
  const { data: blockedAccountsCount = 0 } = useQuery({
    queryKey: ["admin-blocked-accounts-count"],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("account_connection_cooldowns")
        .select("client_id")
        .gt("stage", 0);
      const ids = ((data || []) as any[]).map((r) => r.client_id);
      const live = await fetchLiveness(supabase, ids);
      return ids.filter((id) => live.has(id) && live.get(id) === null).length;
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: enableBadges,
  });

  // Connected accounts with a paying seat but no running campaign
  const { data: idleAccountsCount = 0 } = useQuery({
    queryKey: ["admin-idle-accounts-count"],
    queryFn: async () => (await fetchIdleAccounts(supabase)).filter((r) => r.paying).length,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: enableBadges,
  });

  // Campaign monitor alerts — mirrors Target Refill page "Critical Only" tab
  // (active campaigns where pending===0 OR daysLeft<=3, across managed + CSV)
  const { data: campaignAlerts = 0, refetch: refetchCampaignAlerts } = useQuery({
    queryKey: ["admin-campaign-alerts-count"],
    queryFn: async () => {
      try {
        const ACTIVE_STATUSES = ["active", "starting", "resuming"];
        const { data: campaigns, error } = await supabase
          .from("campaigns")
          .select("id, status, max_connections_daily")
          .is("deleted_at", null)
          .in("status", ACTIVE_STATUSES);

        if (error || !campaigns || campaigns.length === 0) return 0;

        const ids = campaigns.map(c => c.id);
        const { data: stats, error: statsError } = await supabase
          .from("campaign_stats_aggregate")
          .select("campaign_id, pending_count")
          .in("campaign_id", ids);

        if (statsError) return 0;

        const pendingByCampaign: Record<string, number> = {};
        (stats || []).forEach((s: any) => {
          if (s.campaign_id) pendingByCampaign[s.campaign_id] = Number(s.pending_count) || 0;
        });

        let alertCount = 0;
        campaigns.forEach(c => {
          const pending = pendingByCampaign[c.id] || 0;
          const dailyRate = (c as any).max_connections_daily || 10;
          const daysLeft = dailyRate > 0 ? Math.ceil(pending / dailyRate) : 0;
          if (pending === 0 || daysLeft <= 3) alertCount++;
        });
        return alertCount;
      } catch (e) {
        console.error("Error fetching campaign alerts:", e);
        return 0;
      }
    },
    staleTime: 60_000,
    gcTime: 5 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: enableBadges,
  });

  // Realtime: managed_campaign_requests (filtered to relevant statuses).
  // Deferred along with badge queries so it never competes with first paint.
  useEffect(() => {
    if (!enableBadges) return;
    const channel = supabase
      .channel("admin-managed-campaigns-count")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "managed_campaign_requests",
          filter: "status=in.(pending_setup,in_progress)",
        },
        () => refetchPendingManaged()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [refetchPendingManaged, enableBadges]);

  // Realtime: targets — debounced 5s to avoid refetch storms during job execution.
  // Only INSERT events bump the alert count (new pending targets), so we narrow
  // the subscription to drastically reduce inbound message volume.
  const alertsDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!enableBadges) return;
    const channel = supabase
      .channel("admin-campaign-alerts-count")
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "targets" },
        () => {
          if (alertsDebounceRef.current) clearTimeout(alertsDebounceRef.current);
          alertsDebounceRef.current = setTimeout(() => {
            refetchCampaignAlerts();
          }, 5000);
        }
      )
      .subscribe();
    return () => {
      if (alertsDebounceRef.current) clearTimeout(alertsDebounceRef.current);
      supabase.removeChannel(channel);
    };
  }, [refetchCampaignAlerts, enableBadges]);

  const navigationSections: NavigationSection[] = useMemo(() => [
    {
      title: "Overview",
      items: [
        { name: "Dashboard", href: "/admin", icon: Shield },
      ],
    },
    {
      title: "Growth",
      items: [
        { name: "Self-Serve Links", href: "/admin/self-serve-links", icon: Sparkles, onHover: prefetchSelfServeLinks },
      ],
    },
    {
      title: "Customers",
      items: [
        { name: "Workspaces", href: "/admin/organizations", icon: Building2 },
        { name: "CRM Calendar", href: "/admin/crm-calendar", icon: CalendarDays, badge: crmTodayCount },
        { name: "Account Renewals", href: "/admin/account-renewals", icon: CalendarDays },
        { name: "Users", href: "/admin/users", icon: UserCog },
        { name: "Sub Admins", href: "/admin/sub-admins", icon: UserCog },
        { name: "Master Accounts", href: "/admin/master-accounts", icon: Linkedin },
        { name: "Disconnected Accounts", href: "/admin/disconnected-accounts", icon: AlertCircle, badge: disconnectedAccountsCount },
        { name: "Blocked Accounts", href: "/admin/blocked-accounts", icon: AlertCircle, badge: blockedAccountsCount },
        { name: "Idle Accounts", href: "/admin/idle-accounts", icon: Linkedin, badge: idleAccountsCount },
        { name: "Industry Performance", href: "/admin/industry-performance", icon: BarChart3 },
      ],
    },
    {
      title: "Campaigns",
      items: [
        { name: "Automatic Campaigns", href: "/admin/managed-campaigns", icon: Megaphone, badge: pendingManagedCampaigns },
        { name: "Target Refill", href: "/admin/target-refill", icon: Activity, badge: campaignAlerts },
      ],
    },
    {
      title: "Intelligence",
      items: [
        { name: "Profile Engine", href: "/admin/profile-engine", icon: Database },
        {
          name: "System",
          icon: Cog,
          children: [
            { name: "Orphan Campaigns", href: "/admin/orphan-campaigns", icon: Archive },

            { name: "Customer Activity", href: "/admin/customer-activity", icon: UserCog },
            
            { name: "AI Models", href: "/admin/ai-models", icon: Sparkles },
          ],
        },
        { name: "Communications", href: "/admin/communications", icon: MessageSquare },
      ],
    },
    {
      title: "Monitoring",
      items: [
        { name: "Job Queue", href: "/job-queue", icon: ListChecks },
        { name: "System Capacity", href: "/system-capacity", icon: Gauge },
        { name: "Cron Jobs", href: "/admin/cron-jobs", icon: Clock },
      ],
    },
    {
      title: "General",
      items: [
        { name: "Settings", href: "/settings", icon: Settings },
      ],
    },
  ], [disconnectedAccountsCount, blockedAccountsCount, pendingManagedCampaigns, campaignAlerts, prefetchSelfServeLinks]);


  const visibleSections: NavigationSection[] = useMemo(() => {
    if (!isSubAdminView) return navigationSections;
    const keep = (item: any): any | null => {
      if (item.children) {
        const children = item.children.map(keep).filter(Boolean);
        return children.length ? { ...item, children } : null;
      }
      return item.href && isSubAdminAllowedPath(item.href) ? { ...item, badge: item.href === "/admin/crm-calendar" ? item.badge : undefined } : null;
    };
    return navigationSections
      .map((sec) => ({ ...sec, items: sec.items.map(keep).filter(Boolean) }))
      .filter((sec) => sec.items.length > 0);
  }, [navigationSections, isSubAdminView]);

  const handleLogout = useCallback(async () => {
    await signOut();
  }, [signOut]);

  const handleClearCache = useCallback(async () => {
    setIsClearing(true);
    try {
      toast.loading("Clearing cache...", { id: "clear-cache" });
      localStorage.clear();
      sessionStorage.clear();
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.unregister();
        }
      }
      toast.success("Cache cleared! Reloading...", { id: "clear-cache" });
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      console.error("Error clearing cache:", error);
      toast.error("Failed to clear cache", { id: "clear-cache" });
      setIsClearing(false);
    }
  }, []);

  const handleSidebarItemClick = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="hidden lg:flex w-64 flex-col fixed inset-y-0 z-50">
        <SidebarContent
          navigationSections={visibleSections}
          pathname={location.pathname}
          onItemClick={handleSidebarItemClick}
          isClearing={isClearing}
          onClearCache={handleClearCache}
          onLogout={handleLogout}
        />
      </aside>

      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-background border-b border-border z-40 flex items-center px-4 justify-between">
        <div className="flex items-center gap-3">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="-ml-2">
                <Menu className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <SidebarContent
                navigationSections={visibleSections}
                pathname={location.pathname}
                onItemClick={handleSidebarItemClick}
                isClearing={isClearing}
                onClearCache={handleClearCache}
                onLogout={handleLogout}
              />
            </SheetContent>
          </Sheet>
          <span className="font-semibold">Admin Console</span>
        </div>
      </div>

      <main className="flex-1 min-w-0 lg:pl-64 pt-16 lg:pt-0 min-h-screen flex flex-col">
        <header className="hidden lg:flex h-16 items-center justify-between gap-4 border-b border-border bg-background/80 backdrop-blur-sm px-8 sticky top-0 z-30">
          <div className="flex items-center gap-4 flex-1">
            <WorkspaceSearch />
          </div>
          <div className="flex items-center gap-2">
            {isSubAdminView && <Badge variant="outline">Sub Admin</Badge>}
            <Button variant="ghost" size="icon" className="text-muted-foreground">
              <Bell className="w-5 h-5" />
            </Button>
          </div>
        </header>

        <div className="flex-1 min-w-0 p-4 md:p-8 overflow-x-hidden overflow-y-auto">
          <div className="w-full min-w-0 max-w-7xl mx-auto space-y-8">

            <Suspense
              fallback={
                <div className="flex items-center justify-center py-24">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
                </div>
              }
            >
              {children}
            </Suspense>
          </div>
        </div>
      </main>
    </div>
  );
};
